import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE } from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  AlertTriangle, Award, Building2, CalendarDays, CheckCircle2,
  Search, ShieldCheck, UserRound,
} from "lucide-react";

const dateBR = (value) => {
  if (!value) return "—";
  try { return new Date(`${String(value).slice(0, 10)}T12:00:00`).toLocaleDateString("pt-BR"); }
  catch { return value; }
};

const statusLabel = (status) => {
  if (status === "revogado") return "Revogado";
  if (status === "vencido") return "Vencido";
  if (status === "válido") return "Válido";
  return status || "Indeterminado";
};

const statusBand = (status) => {
  if (status === "válido") return "bg-[#123c32] text-white";
  if (status === "revogado") return "bg-[#7f1d1d] text-white";
  return "bg-[#854d0e] text-white";
};

export default function PublicCertificate() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [query, setQuery] = useState(code ? decodeURIComponent(code).toUpperCase() : "");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const current = code ? decodeURIComponent(code).trim().toUpperCase() : "";
    setQuery(current);
    setResult(null);
    setError("");
    if (!current) return;

    let active = true;
    setLoading(true);
    fetch(`${API_BASE}/certificates/verify/${encodeURIComponent(current)}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.detail || "Certificado não encontrado");
        return data;
      })
      .then((data) => active && setResult(data))
      .catch((err) => active && setError(err.message || "Não foi possível validar o certificado"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [code]);

  const search = (event) => {
    event?.preventDefault();
    const normalized = query.trim().toUpperCase();
    if (!normalized) return setError("Informe o código do certificado.");
    navigate(`/certificado/${encodeURIComponent(normalized)}`);
  };

  return (
    <div className="min-h-screen bg-[#f4f7f5] text-[#17211d]">
      <header className="border-b border-[#dce5e0] bg-white">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <div className="grid size-9 place-items-center rounded-xl bg-[#123c32] text-sm font-bold text-white">DM</div>
            <div>
              <div className="font-semibold leading-none">DEMoraes Consultoria</div>
              <div className="mt-1 text-[11px] text-[#66736d]">Validação de certificado</div>
            </div>
          </div>
          <span className="hidden items-center gap-1.5 text-xs text-[#66736d] sm:inline-flex"><ShieldCheck className="size-4 text-[#17613f]" /> Consulta pública</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-10 md:py-16">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <div className="mx-auto grid size-14 place-items-center rounded-2xl bg-[#eaf5ee] text-[#17613f]"><Award className="size-7" /></div>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight md:text-4xl">Verificar certificado</h1>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-[#66736d]">Digite o código individual impresso no certificado para conferir autenticidade, treinamento, participante e validade.</p>
          </div>

          <form onSubmit={search} className="mt-8 flex flex-col gap-2 rounded-2xl border border-[#dce5e0] bg-white p-3 shadow-sm sm:flex-row">
            <Input value={query} onChange={(e) => setQuery(e.target.value.toUpperCase())} placeholder="TRN-2026-XXXXXXXXXXXX-001" className="h-11 flex-1 border-0 bg-[#f7faf8] font-mono uppercase focus-visible:ring-[#17613f]" />
            <Button type="submit" className="h-11 bg-[#123c32] px-6 text-white hover:bg-[#17613f]"><Search className="mr-2 size-4" /> Validar</Button>
          </form>

          {loading && <div className="mt-8 rounded-2xl border border-[#dce5e0] bg-white p-8 text-center text-sm text-[#66736d]">Consultando registro de emissão…</div>}

          {!loading && error && (
            <div className="mt-8 rounded-2xl border border-red-200 bg-red-50 p-6">
              <div className="flex items-center gap-2 font-semibold text-red-700"><AlertTriangle className="size-5" /> Certificado não validado</div>
              <p className="mt-2 text-sm text-red-700/80">{error}</p>
              <p className="mt-3 text-xs text-red-700/65">Confira o código exatamente como aparece no documento. A consulta pública não permite pesquisa por nome ou empresa.</p>
            </div>
          )}

          {!loading && result && (
            <div className="mt-8 overflow-hidden rounded-2xl border border-[#dce5e0] bg-white shadow-[0_18px_50px_rgba(18,60,50,0.08)]">
              <div className={`flex flex-col gap-3 px-6 py-5 sm:flex-row sm:items-center sm:justify-between ${statusBand(result.status)}`}>
                <div>
                  <div className="flex items-center gap-2 text-lg font-semibold"><CheckCircle2 className="size-5" /> Certificado autêntico</div>
                  <div className="mt-1 text-sm opacity-75">
                    {result.status === "revogado"
                      ? "O registro é autêntico, porém o certificado foi formalmente revogado."
                      : "Registro localizado na base de emissões da DEMoraes."}
                  </div>
                </div>
                <span className="w-fit rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider">{statusLabel(result.status)}</span>
              </div>

              <div className="grid gap-0 sm:grid-cols-2">
                <Info icon={UserRound} label="Participante" value={result.participant} />
                <Info icon={Building2} label="Empresa" value={result.client} />
                <Info icon={Award} label="Treinamento" value={`${result.sigla ? `${result.sigla} · ` : ""}${result.training}`} />
                <Info icon={CalendarDays} label="Conclusão" value={dateBR(result.completion_date)} />
                <Info icon={CalendarDays} label="Validade até" value={dateBR(result.expiry_date)} />
                <Info icon={ShieldCheck} label="Emissor" value={result.issuer} />
              </div>

              <div className="border-t border-[#e3e9e5] bg-[#f7faf8] px-6 py-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-[#718079]">Código verificado</div>
                <div className="mt-1 break-all font-mono text-sm font-semibold text-[#123c32]">{result.certificate_code}</div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="border-b border-[#edf1ef] px-6 py-5 sm:[&:nth-child(odd)]:border-r">
      <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.13em] text-[#718079]"><Icon className="size-4 text-[#17613f]" /> {label}</div>
      <div className="mt-2 text-sm font-medium leading-6 text-[#26332d]">{value || "—"}</div>
    </div>
  );
}
